import { useState } from "react";
import { ExternalLink, ShieldCheck } from "lucide-react";
import { AppLink, ClickPopover, EmptyState, TabList, TabPanel, cx, formatBytes, formatDateTime } from "@amber/ui";
import type { ConfidenceView, EvidenceRecord } from "../../contracts/index";
import { capacityText, clock } from "../format";
import type { RecordTab } from "../search";
import { SpeakerContent } from "./Popovers";
import { AttemptsTable } from "./AttemptsTable";
import { RecordTable } from "./RecordTable";
import { CarryDecisionDialog } from "../copies/CarryNotes";
import { ReverseRelationDialog } from "../copies/ReverseRelationDialog";

function Score({ c }: { c: ConfidenceView | null }) {
  if (!c || c.value === null) return <span className="text-muted">{c?.reason ?? "—"}</span>;
  return (
    <span>
      <span className="font-mono">{c.value.toFixed(2)}</span> <span className="text-md text-muted">{c.label}</span>
    </span>
  );
}

type SpeakerRow = EvidenceRecord["speakers"][number];

function noProfileText(s: SpeakerRow): string {
  if (s.kind === "ambiguous") return `Ambiguous · ${String(s.profile_match.candidates.length)} profiles`;
  return s.kind === "unresolved" ? "—" : "No profile";
}

function namingEvidenceText(e: SpeakerRow["naming"]["evidence"][number], isDocument: boolean): string {
  if (e.at_ms !== null) return `“${e.text}” at ${clock(e.at_ms)}`;
  return isDocument ? `Printed label “${e.text}”` : `“${e.text}” in the metadata`;
}

function SpeakersTab({ record, onSeek }: { record: EvidenceRecord; onSeek: (ms: number) => void }) {
  if (record.speakers.length === 0)
    return (
      <EmptyState title="Speakers are not identified yet">
        Speaker names, profile matches and confidences appear here after the Speakers stage.
      </EmptyState>
    );
  return (
    <RecordTable
      label="Speakers"
      head={[
        "Speaker",
        "Role",
        "Profile",
        "Name",
        "Profile match",
        "Attribution (lowest)",
        "Evidence",
        record.document ? "Words" : "Speaking time",
        record.document ? "Contributions" : "Turns",
      ]}
    >
      {record.speakers.map((s) => (
        <tr key={s.id}>
          <td>
            <ClickPopover
              trigger={<span className={cx("font-strong", !s.name && "text-muted")}>{s.display}</span>}
              label={`${s.display}, details`}
            >
              <SpeakerContent speaker={s} onSeek={onSeek} />
            </ClickPopover>
          </td>
          <td className="text-muted">{s.role ? `${s.role.value}${s.role.inferred ? " (inferred)" : ""}` : "—"}</td>
          <td>
            {s.profile ? (
              <AppLink href="/profiles">{s.profile.name}</AppLink>
            ) : (
              <span className="text-muted">{noProfileText(s)}</span>
            )}
            {s.profile ? <span className="text-md text-muted"> · {s.kind_label}</span> : null}
            {s.capacity ? <div className="text-md text-muted">{capacityText(s.capacity)}</div> : null}
          </td>
          <td>
            <Score c={s.naming.confidence} />
          </td>
          <td>
            <Score c={s.profile_match.confidence} />
          </td>
          <td>
            <Score c={s.lowest_attribution} />
          </td>
          <td className="max-w-[240px] text-md text-muted">
            {s.naming.evidence.length > 0
              ? s.naming.evidence.map((e) => namingEvidenceText(e, Boolean(record.document))).join("; ")
              : (s.naming.reason ?? "—")}
          </td>
          <td className="font-mono">{record.document ? String(s.speaking_ms) : clock(s.speaking_ms)}</td>
          <td className="font-mono">{s.turn_count}</td>
        </tr>
      ))}
    </RecordTable>
  );
}

function SourceTab({ record }: { record: EvidenceRecord }) {
  const s = record.source;
  return (
    <>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-6 px-4 pb-4 max-compact:grid-cols-1 max-compact:px-2">
        <div>
          <h3 className="m-0 mb-1.5 text-ui font-semibold">Origin</h3>
          <dl className="m-0 grid grid-cols-[110px_1fr] gap-y-1 text-md [&_dd]:m-0 [&_dd]:break-all [&_dt]:text-muted">
            <dt>Original</dt>
            <dd>
              <a href={s.original_url} target="_blank" rel="noreferrer noopener" className="text-link">
                {s.original_url}
              </a>
            </dd>
            <dt>Canonical</dt>
            <dd>{s.canonical_url}</dd>
            <dt>Retrieved from</dt>
            <dd>{s.retrieval_url ?? "Not retrieved"}</dd>
          </dl>
          <h3 className="m-0 mt-3 mb-1.5 text-ui font-semibold">Availability of the original</h3>
          {s.availability.length === 0 ? (
            <p className="m-0 text-md text-muted">Not checked yet</p>
          ) : (
            <ul className="m-0 list-none p-0 text-md">
              {s.availability.map((a) => (
                <li key={`${a.at}-${a.state}`}>
                  {formatDateTime(a.at)} · {a.label}
                  <span className="text-muted"> · source version {a.version}</span>
                </li>
              ))}
            </ul>
          )}
          <h3 className="m-0 mt-3 mb-1.5 text-ui font-semibold">Rights as observed</h3>
          {s.rights ? (
            <dl className="m-0 grid grid-cols-[110px_1fr] gap-y-1 text-md [&_dd]:m-0 [&_dt]:text-muted">
              <dt>Declared terms</dt>
              <dd>{s.rights.declared_terms ?? "Unknown"}</dd>
              <dt>Holder</dt>
              <dd>{s.rights.holder ?? "Unknown"}</dd>
              <dt>Permission</dt>
              <dd>{s.rights.permission === "not_obtained" ? "Not obtained" : s.rights.permission}</dd>
              <dt>Unknown</dt>
              <dd>{s.rights.unknowns.map((u) => u.replace(/_/g, " ")).join(", ") || "Nothing"}</dd>
              <dt>Observed</dt>
              <dd>{formatDateTime(s.rights.observed_at)}</dd>
            </dl>
          ) : (
            <p className="m-0 text-md text-muted">No rights record yet: nothing is archived.</p>
          )}
          <p className="m-0 mt-1.5 text-md text-muted">Rights are informational and never a failure.</p>
        </div>
        <div className="max-compact:mt-3">
          <h3 className="m-0 mb-1.5 text-ui font-semibold">Captured files</h3>
          {s.files.length === 0 ? (
            <p className="m-0 text-md text-muted">Nothing was archived.</p>
          ) : (
            <ul className="m-0 list-none p-0">
              {s.files.map((f) => (
                <li key={f.id} className="flex items-baseline gap-2 border-b border-divider py-1.5 text-md">
                  <span className="font-strong">{f.label}</span>
                  <span className="min-w-0 flex-1 truncate text-muted">
                    {f.detail} · {formatBytes(f.size_bytes)} · source version {f.version}
                  </span>
                  {f.href ? (
                    <>
                      <a href={f.href} target="_blank" rel="noreferrer noopener" className="text-link">
                        View
                      </a>
                      <a href={f.href} download className="text-link">
                        Download
                      </a>
                    </>
                  ) : (
                    <span className="text-muted">{f.notice}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <AttemptsTable attempts={s.attempts} />
    </>
  );
}

function ProcessingTab({ record }: { record: EvidenceRecord }) {
  const p = record.processing;
  return (
    <>
      <RecordTable
        label="Processing stages"
        head={["Stage", "Status", "Details", "Finished", "Duration", "Attempts", "Cost"]}
      >
        {p.stages.map((s) => (
          <tr key={s.stage}>
            <td className="font-strong">{s.name}</td>
            <td className={cx(s.state === "failed" && "text-attention", s.state === "waiting" && "text-waiting")}>
              {s.state === "not_applicable" ? "Not applicable" : s.state.replace(/_/g, " ")}
            </td>
            <td className="text-muted">{s.text}</td>
            <td className="text-muted">{s.finished_at ? formatDateTime(s.finished_at) : "—"}</td>
            <td className="font-mono text-muted">{s.duration_ms === null ? "—" : clock(s.duration_ms)}</td>
            <td className="font-mono text-muted">
              {s.attempts.used} of {s.attempts.allowed}
            </td>
            <td className="text-md text-muted">{s.cost ? `$${s.cost.amount_decimal}` : "—"}</td>
          </tr>
        ))}
      </RecordTable>
      <p className="m-0 flex items-center gap-3 px-6 pb-4 text-md text-muted">
        Item total ${p.total_cost.amount_decimal}
        {p.stages[0]?.engine_href ? (
          <a
            href={p.stages[0].engine_href}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-link"
          >
            Open engine run <ExternalLink size={11} aria-hidden="true" />
          </a>
        ) : null}
      </p>
    </>
  );
}

function HistoryTab({ record, onOpenVersion }: { record: EvidenceRecord; onOpenVersion: (version: number) => void }) {
  const [reverting, setReverting] = useState<string | null>(null);
  const [reversing, setReversing] = useState<string | null>(null);
  const canRevert = record.actions.copies.enabled && record.pinned === null;
  if (record.history.length === 0)
    return (
      <EmptyState title="No history yet">
        Captures, publications, corrections and access changes appear here.
      </EmptyState>
    );
  return (
    <>
      {reversing ? (
        <ReverseRelationDialog
          itemId={record.item.id}
          relationId={reversing}
          onClose={() => {
            setReversing(null);
          }}
        />
      ) : null}
      {reverting ? (
        <CarryDecisionDialog
          itemId={record.item.id}
          title="Revert the carried-over correction for this copy…"
          action="Revert"
          path={`/api/evidence/carry-overs/${reverting}/revert`}
          onClose={() => {
            setReverting(null);
          }}
        />
      ) : null}
      <RecordTable label="History" head={["When", "What", "Who", "Details", ""]}>
        {record.history.map((h, i) => (
          <tr key={`${h.at}-${String(i)}`}>
            <td className="whitespace-nowrap text-muted">{formatDateTime(h.at)}</td>
            <td className="font-strong">
              {h.kind === "check" ? (
                <ShieldCheck size={13} aria-hidden="true" className="mr-1 inline-block align-[-2px] text-muted" />
              ) : null}
              {h.label}
              {h.version ? <span className="font-normal text-muted"> · version {h.version}</span> : null}
            </td>
            <td className="text-muted">{h.by ?? "Automatic"}</td>
            <td className="text-muted">{h.detail ?? "—"}</td>
            <td className="whitespace-nowrap">
              {h.revert ? (
                <button
                  type="button"
                  className="mr-3 text-md text-link disabled:text-muted"
                  disabled={!canRevert}
                  title={canRevert ? undefined : "Only Editors and above can revert carried-over corrections"}
                  onClick={() => {
                    setReverting(h.revert);
                  }}
                >
                  Revert…
                </button>
              ) : null}
              {h.reverse ? (
                <button
                  type="button"
                  className="mr-3 text-md text-link disabled:text-muted"
                  disabled={!canRevert}
                  title={canRevert ? undefined : "Only Editors and above can reverse merges and splits"}
                  onClick={() => {
                    setReversing(h.reverse);
                  }}
                >
                  Reverse…
                </button>
              ) : null}
              {h.version && h.version > 1 ? (
                <button
                  type="button"
                  className="text-md text-link"
                  onClick={() => {
                    onOpenVersion((h.version ?? 2) - 1);
                  }}
                >
                  Open version before this
                </button>
              ) : null}
            </td>
          </tr>
        ))}
      </RecordTable>
    </>
  );
}

export function RecordTabs({
  record,
  tab,
  onTab,
  onSeek,
  onOpenVersion,
}: {
  record: EvidenceRecord;
  tab: RecordTab;
  onTab: (t: RecordTab) => void;
  onSeek: (ms: number) => void;
  onOpenVersion: (v: number) => void;
}) {
  return (
    <section
      aria-label="Record details"
      className="mx-4 mb-4 rounded-xl border border-divider bg-bg pt-1 max-compact:mx-2"
    >
      <div className="relative">
        <span className="absolute top-2.5 right-4 text-md text-muted">
          {record.pinned ? `Showing version ${String(record.pinned.version)}` : "Latest version"}
        </span>
        <TabList
          label="Record details"
          value={tab}
          onChange={onTab}
          items={[
            { value: "speakers", label: "Speakers", count: record.speakers.length },
            { value: "source", label: "Source & archive" },
            { value: "processing", label: "Processing" },
            { value: "history", label: "History", count: record.history.length },
          ]}
        >
          <TabPanel value="speakers">
            <SpeakersTab record={record} onSeek={onSeek} />
          </TabPanel>
          <TabPanel value="source">
            <SourceTab record={record} />
          </TabPanel>
          <TabPanel value="processing">
            <ProcessingTab record={record} />
          </TabPanel>
          <TabPanel value="history">
            <HistoryTab record={record} onOpenVersion={onOpenVersion} />
          </TabPanel>
        </TabList>
      </div>
    </section>
  );
}
